import React, { FunctionComponent, useCallback, useEffect, useState } from "react";
import styles from "./Employees.module.scss";
import EmployeesProps from "./EmployeesProps.interface";

import Employee from "./components/Employee/Employee";
import EmployeeProps from "./components/Employee/EmployeeProps.interface";
import fetchEmployees from "./hooks/fetchEmployees";

import BubbleBackground from "components/BubbleBackground/BubbleBackground";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import Paragraph from "components/Paragraph/Paragraph";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";
import useFilter from "blocks/hooks/useFilter";
import { useHistory } from "react-router-dom";
import isBrowserContext from "helpers/isBrowserContext";

const Employees: FunctionComponent<EmployeesProps> = (props) => {
    const history = useHistory();

    const wrapperClasses = getWrapperClasses(props.settings);
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    const [filters, filterViewState] = useFilter(props.content.filter);
    const [currentFilters, setCurrentFilters] = useState<any[]>();

    const [items, itemViewState] = fetchEmployees(props.content.tags);

    const filteredItems = useCallback(() => {
        const result = items ?? [];

        return result?.filter((item: any) => {
            const givenFilters = item.filters && item.filters.map((filter: any) => filter._id.toString());
            const currentFilter = currentFilters && currentFilters.length > 0 ? currentFilters[0] : undefined;

            return currentFilter ? givenFilters?.includes(currentFilter) : true;
        });
    }, [items, currentFilters]);

    const handleTagClick = (filter: any) => {
        const filterId = filter.id.toString();

        // Setting current filter
        if (typeof (filterId) !== "undefined" && filterId !== null) {
            if (currentFilters && currentFilters.length > 0 && currentFilters[0] === filterId) {
                setCurrentFilters([]);

                // Setting URL Params
                history.replace({
                    search: undefined,
                });
            }
            else {
                setCurrentFilters([filterId]);

                // Setting URL Params
                history.replace({
                    pathname: location.pathname,
                    search: `?f=${filter.id}`,
                    hash: location.hash
                });
            }
        }
    };

    useEffect(() => {
        const query = new URLSearchParams(history.location.search);
        const filter = query.get("f");

        // Setting current filter
        if (typeof (filter) !== "undefined" && filter !== null) {
            setCurrentFilters([filter]);
        }
    }, []);

    return (
        <div className={combineClasses([styles.wrapper, ...wrapperClasses])} id={identifier}>
            <div className={styles.container}>
                <div className={styles.top_content}>
                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading className={styles.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight}>{props.content.headline}</DynamicHeading>
                    ) : null}

                    {/* Text */}
                    {props.content.bodyText ? (
                        <Paragraph innerHTML={props.content.bodyText} />
                    ) : null}
                </div>

                {/* Filters */}
                {isBrowserContext() && filterViewState === "LOADED" && filters && filters.length > 0 ? (
                    <div className={styles.tags}>
                        {filters && filters?.map((filter: any) => {
                            const currentFilterClass = currentFilters?.includes(filter.id.toString()) ? styles.is_active : undefined;

                            return (
                                <button key={filter.id} tabIndex={0} onClick={() => handleTagClick(filter)} className={combineClasses([styles.tag, currentFilterClass])}>
                                    {filter.label}
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                {itemViewState === "LOADING" ? (
                    <div>Loading...</div>
                ) : null}

                {itemViewState === "LOADED" ? (
                    <div className={styles.list}>
                        {filteredItems()?.map((item: EmployeeProps) => {
                            return (
                                <Employee key={item._id} {...item} />
                            );
                        })}
                    </div>
                ) : null}

                {itemViewState === "EMPTY" ? (
                    <div>Empty</div>
                ) : null}

                {itemViewState === "ERROR" ? (
                    <div>Error</div>
                ) : null}

            </div>

            {props.settings.bubbleBackground && props.settings.bubbleBackground.length > 0 ? (
                <BubbleBackground {...props.settings.bubbleBackground[0]} />
            ) : null}
        </div>
    );
};

export default Employees;